import { useState, useCallback, useEffect, useMemo } from "react";
import { sortArrayByKey, useShowError } from "JS/Helpers";
import { useSnackbar } from "notistack";
import { useAppDispatch, useAppSelector } from "JS/Redux/Store";
import {
    AddToCart,
    Members,
    SendGift,
    PurchasableGift,
    GiftToSend,
    GiftGiven,
    ReceivedGift,
    ContentType,
} from "JS/Models/Media";
import {
    setAudiosToSend,
    setGiftDetails,
    setGlobalGifts,
    setPurchasableMemberAudios,
    setPurchasableMemberVideos,
    setPurchasableProspectAudios,
    setPurchasableProspectVideos,
    setReceivedMemberAudios,
    setReceivedMemberVideos,
    setReceivedProspectAudios,
    setReceivedProspectVideos,
    setVideosToSend,
} from "JS/Redux/Gift";
import {
    EventNames,
    EventActions,
    EventCategories,
    User,
    ContentDetails,
    OldUser,
} from "JS/Models";
import { useFirebaseLogger } from "./Firebase";
import { GiftService } from "JS/Services/Gift";
import { useGlobalVideos, useSyncOfflineVideos } from "./Video";
import { useGlobalAudios, useSyncOfflineAudios } from "./Audio";
import { config } from "JS/Config";
import { useGiftDashboardSent } from "./Firebase/GiftDashboard";
import { toSentGiftType } from "JS/Models/Firebase/GiftDashboard";
import { messaging } from "JS/Helpers/UserMessaging";
import { useContentDetails } from "./Media";
import { AxiosResponse } from "axios";

const service = new GiftService();

export const useAudioGifts = (contentType: string, skip?: boolean) => {
    const [loading, setLoading] = useState(false);
    const { setPurchasableMemberAudios, setPurchasableProspectAudios } =
        useGlobalGifts();
    const { showError } = useShowError();
    const dispatch = useAppDispatch();

    const refetch = useCallback(() => {
        setLoading(true);
        return service
            .getPurchasableGifts(contentType)
            .then((val) => {
                contentType === "GiftMP3"
                    ? setPurchasableMemberAudios(val.data)
                    : setPurchasableProspectAudios(val.data);
                return val.data;
            })
            .catch((err) => {
                showError(err?.message);
                return [];
            })

            .finally(() => {
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        if (!skip) {
            refetch();
        }
    }, [skip]);

    return {
        refetch,
        loading,
    };
};

export const useVideoGifts = (contentType: string, skip?: boolean) => {
    const [loading, setLoading] = useState(false);
    const { setPurchasableMemberVideos, setPurchasableProspectVideos } =
        useGlobalGifts();
    const { showError } = useShowError();
    const dispatch = useAppDispatch();

    const refetch = useCallback(() => {
        setLoading(true);

        return service
            .getPurchasableGifts(contentType)
            .then((val) => {
                contentType === "GiftVideo"
                    ? setPurchasableMemberVideos(val.data)
                    : setPurchasableProspectVideos(val.data);
                return val.data;
            })
            .catch((err) => {
                showError(err.message);
                return [];
            })

            .finally(() => {
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        if (!skip) {
            refetch();
        }
    }, [skip]);

    return {
        refetch,
        loading,
    };
};

export const useAudiosToSend = (contentType: string, skip?: boolean) => {
    const [loading, setLoading] = useState(false);
    const { setAudiosToSend: setToSendAudios } = useGlobalGifts();
    const { showError } = useShowError();

    const refetch = useCallback(() => {
        setLoading(true);
        return service
            .getToSendMediaGifts(contentType)
            .then((response) => {
                setToSendAudios(response.data);
                return response.data;
            })
            .catch((err) => {
                showError(err?.message);
                return [];
            })

            .finally(() => {
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        if (!skip) {
            refetch();
        }
    }, [skip]);

    return {
        refetch,
        loading,
    };
};

export const useVideosToSend = (contentType: string, skip?: boolean) => {
    const [loading, setLoading] = useState(false);
    const { setVideosToSend: setToSendVideos } = useGlobalGifts();
    const { showError } = useShowError();

    const refetch = useCallback(() => {
        setLoading(true);

        return service
            .getToSendMediaGifts(contentType)
            .then((response) => {
                setToSendVideos(response.data);
                return response.data;
            })
            .catch((err) => {
                showError(err.message);
                return [];
            })

            .finally(() => {
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        if (!skip) {
            refetch();
        }
    }, [skip]);

    return {
        refetch,
        loading,
    };
};

const getSendingDate = (list: ReceivedGift[], nid: string) => {
    return list.find((g) => g.nid === nid)?.sender?.sending_date_time;
};

export const useReceivedMemberAudioGifts = (skip?: boolean) => {
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const { receivedMemberAudios, setReceivedMemberAudios } = useGlobalGifts();
    const { showError } = useShowError();

    const { playedAudios } = useGlobalAudios();
    const playedAudiosList =
        playedAudios && playedAudios[config.user.memberId]
            ? playedAudios[config.user.memberId]
            : [];

    const { syncOfflineAudios } = useSyncOfflineAudios();

    const isGift = useCallback(
        (nid: string) => {
            return !!receivedMemberAudios?.find((a) => a.nid === nid);
        },
        [receivedMemberAudios],
    );

    const getGiftDate = useCallback(
        (nid: string) => getSendingDate(receivedMemberAudios, nid),
        [receivedMemberAudios],
    );

    const refetch = useCallback(() => {
        setLoading(true);

        return service
            .getReceivedGifts("GiftMP3")
            .then((response) => {
                const allGifts: ReceivedGift[] = response.data.map((item) => {
                    if (
                        playedAudiosList.filter((x) => x.nid === item.nid)
                            .length > 0
                    ) {
                        return {
                            ...item,
                            played: playedAudiosList.filter(
                                (x) => x.nid === item.nid,
                            )[0].played,
                            isReceived: true,
                        };
                    } else {
                        return {
                            ...item,
                            isReceived: true,
                            played: "0",
                        };
                    }
                });
                syncOfflineAudios(allGifts);
                const toSave = sortArrayByKey(allGifts, "title", "ASC");
                setReceivedMemberAudios(toSave);

                return response.data;
            })
            .catch((err) => {
                showError(err.message);
                return [];
            })

            .finally(() => {
                setLoading(false);
                setLoaded(true);
            });
    }, []);

    useEffect(() => {
        if (!skip) refetch();
    }, [skip]);

    return {
        refetch,
        loading,
        loaded,
        isGift,
        getGiftDate,
        receivedMemberAudios,
    };
};

export const useReceivedMemberVideoGifts = (skip?: boolean) => {
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const { receivedMemberVideos, setReceivedMemberVideos } = useGlobalGifts();

    const { playedVideos } = useGlobalVideos();
    const playedVideosList =
        playedVideos && playedVideos[config.user.memberId]
            ? playedVideos[config.user.memberId]
            : [];

    const { syncOfflineVideos } = useSyncOfflineVideos();

    const { showError } = useShowError();

    const isGift = useCallback(
        (nid: string) => {
            return !!receivedMemberVideos?.find((v) => v.nid === nid);
        },
        [receivedMemberVideos],
    );

    const getGiftDate = useCallback(
        (nid: string) => getSendingDate(receivedMemberVideos, nid),
        [receivedMemberVideos],
    );

    const refetch = useCallback(() => {
        setLoading(true);

        return service
            .getReceivedGifts("GiftVideo")
            .then((response) => {
                const allGifts: ReceivedGift[] = response.data.map((item) => {
                    const playedData = playedVideosList?.find(
                        (x) => x.nid === item.nid,
                    );
                    return playedData
                        ? {
                              ...item,
                              played: playedData.played,
                              isReceived: true,
                          }
                        : {
                              ...item,
                              isReceived: true,
                              played: "0",
                          };
                });

                syncOfflineVideos(allGifts);
                const toSave = sortArrayByKey(allGifts, "title", "ASC");
                setReceivedMemberVideos(toSave);

                return response.data;
            })
            .catch((err) => {
                showError(err.message);
                return [];
            })

            .finally(() => {
                setLoading(false);
                setLoaded(true);
            });
    }, []);

    useEffect(() => {
        if (!skip) {
            refetch();
        }
    }, [skip]);

    return {
        refetch,
        loading,
        loaded,
        isGift,
        getGiftDate,
        receivedMemberVideos,
    };
};

export const useProspectingUser = (skip?: boolean) => {
    const [loading, setLoading] = useState(false);
    const [isProsUser, setIsProsUser] = useState(null);

    const refetch = useCallback(() => {
        setLoading(true);
        return service
            .isProspectingUser()
            .then((res) => {
                const status = res.data;
                setIsProsUser(status);
                return status;
            })
            .catch((err) => {
                return null;
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        if (!skip) refetch();
    }, [skip]);

    return {
        refetch,
        loading,
        isProsUser,
    };
};

export const useReceivedProspectAudioGifts = (skip?: boolean) => {
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const { playedAudios } = useGlobalAudios();
    const playedAudiosList =
        playedAudios && playedAudios[config.user.memberId]
            ? playedAudios[config.user.memberId]
            : [];

    const { syncOfflineAudios } = useSyncOfflineAudios();

    const { receivedProspectAudios, setReceivedProspectAudios } =
        useGlobalGifts();

    const refetch = useCallback(() => {
        setLoading(true);

        return service
            .getReceivedGifts("GiftProsMP3")
            .then((response) => {
                const allGifts: ReceivedGift[] = response.data.map((item) => {
                    const playedAudios = playedAudiosList.filter(
                        (x) => x.nid === item.nid,
                    );
                    return {
                        ...item,
                        played:
                            playedAudios.length > 0
                                ? playedAudios[0].played
                                : "0",
                        isReceived: true,
                    };
                });
                syncOfflineAudios(allGifts);
                setReceivedProspectAudios(
                    sortArrayByKey(allGifts, "title", "ASC"),
                );

                return response.data;
            })
            .catch((err) => {
                return [];
            })

            .finally(() => {
                setLoading(false);
                setLoaded(true);
            });
    }, []);

    useEffect(() => {
        if (!skip) {
            refetch();
        }
    }, [skip]);

    return {
        refetch,
        loading,
        loaded,
        receivedProspectAudios,
    };
};

export const useReceivedProspectVideoGifts = (skip?: boolean) => {
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const { playedVideos } = useGlobalVideos();
    const playedVideosList =
        playedVideos && playedVideos[config.user.memberId]
            ? playedVideos[config.user.memberId]
            : [];

    const { syncOfflineVideos } = useSyncOfflineVideos();

    const dispatch = useAppDispatch();

    const refetch = useCallback(() => {
        setLoading(true);
        return service
            .getReceivedGifts("GiftProsVideo")
            .then(async (response) => {
                const allGifts = response.data.map((item) => {
                    if (
                        playedVideosList.filter((x) => x.nid === item.nid)
                            .length > 0
                    ) {
                        return {
                            ...item,
                            played: playedVideosList.filter(
                                (x) => x.nid === item.nid,
                            )[0].played,
                            isReceived: true,
                        };
                    } else {
                        return {
                            ...item,
                            isReceived: true,
                            played: "0",
                        };
                    }
                });
                syncOfflineVideos(allGifts);

                dispatch(
                    setGlobalGifts({
                        receivedProspectVideoGifts: sortArrayByKey(
                            allGifts,
                            "title",
                            "ASC",
                        ),
                    }),
                );

                return response.data;
            })
            .catch((err) => {
                return [];
            })

            .finally(() => {
                setLoading(false);
                setLoaded(true);
            });
    }, []);

    useEffect(() => {
        if (!skip) {
            refetch();
        }
    }, [skip]);

    return {
        refetch,
        loading,
        loaded,
        receivedProspectVideoGifts: useAppSelector((state) => state.gifts)
            ?.receivedProspectVideoGifts,
    };
};

export const useSendGifts = () => {
    const [loading, setLoading] = useState(false);

    const { enqueueSnackbar } = useSnackbar();
    const { logFirebaseEvent } = useFirebaseLogger();
    const { logGiftSent } = useGiftDashboardSent();
    const { showError } = useShowError();

    const sendGiftToUser = useCallback(
        async (
            gifts: SendGift[],
            selectedMembers: Members[],
            contentType: string,
        ) => {
            setLoading(true);
            try {
                const skuIds = gifts.map((c) => c.sku);
                const val = await service.sendGift(
                    skuIds,
                    selectedMembers.map((s) => s.member_id),
                    contentType,
                );
                gifts.forEach((gift) => {
                    selectedMembers.forEach((member) => {
                        logFirebaseEvent(getEventName(gift.category), {
                            action: EventActions.GIFT_SENT,
                            skuId: gift.sku,
                            receiverId: member.member_id,
                            nId: gift.nid,
                            contentTitle: gift?.title,
                        });
                        logGiftSent(
                            {
                                sku: gift.sku,
                                nid: gift.nid,
                                contentTitle: gift?.title,
                                receiverFirstName: member.first_name,
                                receiverLastName: member.last_name,
                            },
                            toSentGiftType(gift.category),
                        );
                    });
                });

                enqueueSnackbar(messaging.gifts.successTitle, {
                    variant: "success",
                });
                return val;
            } catch (err) {
                showError(err.response.data.message);
                throw err;
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    const getEventName = (category: string) => {
        if (category === EventCategories.GIFT_AUDIOS) {
            return EventNames.GIFT_AUDIO_GIFT_SENT;
        }

        if (category === EventCategories.GIFT_VIDEOS) {
            return EventNames.GIFT_VIDEO_GIFT_SENT;
        }

        if (category === EventCategories.GIFT_COURSE) {
            return EventNames.GIFT_COURSE_SENT;
        }
    };

    return {
        sendGiftToUser,
        loading,
    };
};

export const useAllMembersOfGroup = (skip?: boolean) => {
    const [loading, setLoading] = useState(false);
    const [members, setMembers] = useState<Members[]>([]);
    const { showError } = useShowError();

    const refetch = useCallback(() => {
        setLoading(true);

        return service
            .allMembersOfGroup()
            .then((val) => {
                setMembers(val.data);
                return val;
            })
            .catch((err) => {
                showError(err.message);
                return null as AxiosResponse<AddToCart>;
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        if (!skip) {
            refetch();
        }
    }, [skip]);

    return {
        members,
        refetch,
        loading,
    };
};

export const useRemainingQuantity = (
    memberSku?: string,
    prospectSku?: string,
) => {
    const [loading, setLoading] = useState(false);
    const [quantity, setQuantity] = useState({
        memberQuantity: 0,
        prospectQuantity: 0,
    });
    const { showError } = useShowError();
    const fetch = (memberSku?: string, prospectSku?: string) => {
        if (!!prospectSku) {
            setLoading(true);
            service
                .getProspectGiftQuantity(prospectSku)
                .then((res) => {
                    setQuantity((eq) => ({
                        ...eq,
                        prospectQuantity: res?.data?.quantity || 0,
                    }));
                    return res?.data?.quantity;
                })
                .catch(showError)
                .finally(() => setLoading(false));
        }
        if (!!memberSku) {
            setLoading(true);
            service
                .getMemberGiftQuantity(memberSku)
                .then((res) => {
                    setQuantity((eq) => ({
                        ...eq,
                        memberQuantity: res?.data?.quantity || 0,
                    }));
                    return res?.data?.quantity;
                })
                .catch(showError)
                .finally(() => setLoading(false));
        }
    };

    useEffect(() => {
        fetch(memberSku, prospectSku);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [memberSku, prospectSku]);

    return {
        loading,
        quantity,
        fetch,
    };
};

export const useSyncDYS = () => {
    const [signingIn, setSigningIn] = useState(false);
    const [error, setError] = useState<{ error: string }>();
    const [success, setSuccess] = useState(false);

    const signIn = (userName: string, password: string) => {
        setSigningIn(true);
        return service
            .syncDYS(userName, password)
            .then((val) => {
                setSuccess(true);
            })
            .catch((e) =>
                setError({
                    error: e
                        ? e.response.data.response.message
                        : "Something went wrong. Please try again",
                }),
            )
            .finally(() => setSigningIn(false));
    };

    return {
        signingIn,
        success,
        error,
        signIn,
    };
};

export const useGiftDetails = (nid: string, contentType: ContentType) => {
    const { setGiftDetail } = useGlobalGifts();
    const { loading, detail, fetch } = useContentDetails(nid, contentType);

    useEffect(() => {
        if (!!nid && !!contentType) fetch();
    }, [nid, contentType]);

    useEffect(() => {
        if (detail) setGiftDetail(nid, detail);
    }, [detail]);

    return {
        loading,
    };
};

export const useGlobalGifts = () => {
    const dispatch = useAppDispatch();

    const allData = useAppSelector((state) => state.gifts);

    return {
        purchasableMemberAudios: useAppSelector((state) => state.gifts)
            ?.purchasableMemberAudios,
        purchasableMemberVideos: useAppSelector((state) => state.gifts)
            ?.purchasableMemberVideos,
        purchasableProspectAudios: useAppSelector((state) => state.gifts)
            ?.purchasableProspectAudios,
        purchasableProspectVideos: useAppSelector((state) => state.gifts)
            ?.purchasableProspectVideos,
        audiosToSend: useAppSelector((state) => state.gifts)?.audiosToSend,
        videosToSend: useAppSelector((state) => state.gifts)?.videosToSend,
        receivedMemberAudios: useAppSelector((state) => state.gifts)
            ?.receivedMemberAudios,
        receivedMemberVideos: useAppSelector((state) => state.gifts)
            ?.receivedMemberVideos,
        receivedProspectAudios: useAppSelector((state) => state.gifts)
            ?.receivedProspectAudios,
        receivedProspectVideos: useAppSelector((state) => state.gifts)
            ?.receivedProspectVideos,
        setGlobalGifts: (gifts) => {
            dispatch(
                setGlobalGifts({
                    ...allData,
                    ...gifts,
                }),
            );
        },
        setPurchasableMemberAudios: (gifts: PurchasableGift[]) =>
            dispatch(
                setPurchasableMemberAudios({
                    ...allData,
                    purchasableMemberAudios: gifts,
                }),
            ),
        setPurchasableMemberVideos: (gifts: PurchasableGift[]) =>
            dispatch(
                setPurchasableMemberVideos({
                    ...allData,
                    purchasableMemberVideos: gifts,
                }),
            ),
        setPurchasableProspectAudios: (gifts: PurchasableGift[]) =>
            dispatch(
                setPurchasableProspectAudios({
                    ...allData,
                    purchasableProspectAudios: gifts,
                }),
            ),
        setPurchasableProspectVideos: (gifts: PurchasableGift[]) =>
            dispatch(
                setPurchasableProspectVideos({
                    ...allData,
                    purchasableProspectVideos: gifts,
                }),
            ),
        setAudiosToSend: (gifts: GiftToSend[]) =>
            dispatch(
                setAudiosToSend({
                    ...allData,
                    audiosToSend: gifts,
                }),
            ),
        setVideosToSend: (gifts: GiftToSend[]) =>
            dispatch(
                setVideosToSend({
                    ...allData,
                    videosToSend: gifts,
                }),
            ),
        setReceivedMemberAudios: (gifts: ReceivedGift[]) => {
            dispatch(
                setReceivedMemberAudios({
                    ...allData,
                    receivedMemberAudios: gifts,
                }),
            );
        },
        setReceivedMemberVideos: (gifts: ReceivedGift[]) => {
            dispatch(
                setReceivedMemberVideos({
                    ...allData,
                    receivedMemberVideos: gifts,
                }),
            );
        },
        setReceivedProspectAudios: (gifts: ReceivedGift[]) => {
            dispatch(
                setReceivedProspectAudios({
                    ...allData,
                    receivedProspectAudios: gifts,
                }),
            );
        },
        setReceivedProspectVideos: (gifts: ReceivedGift[]) => {
            dispatch(
                setReceivedProspectVideos({
                    ...allData,
                    receivedProspectVideos: gifts,
                }),
            );
        },
        setGiftDetail: (nid: string, detail: ContentDetails) => {
            const map = !!allData?.giftDetails
                ? { ...allData.giftDetails }
                : {};

            map[nid] = detail;
            dispatch(setGiftDetails(map));
        },
        getGiftDetails: (nid: string) => {
            const details = allData?.giftDetails;
            return !!details ? details[nid] : undefined;
        },
    };
};

export type GiftsGivenFetchType = "members" | "gifts";

export const useGiftsGiven = (
    fetchType: GiftsGivenFetchType,
    query: string,
    memberId?: string,
) => {
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [members, setMembers] = useState<OldUser[]>([]);
    const [gifts, setGifts] = useState<GiftGiven[]>([]);
    const { showError } = useShowError();

    const fetchMemberList = () => {
        setLoading(true);
        service
            .getGiftReceivers()
            .then((r) => setMembers(r.data))
            .catch(showError)
            .finally(() => {
                setLoading(false);
                setLoaded(true);
            });
    };

    const fetchGiftList = () => {
        setLoading(true);
        service
            .getGiftsGiven(memberId)
            .then((r) => setGifts(r.data))
            .catch(showError)
            .finally(() => {
                setLoading(false);
                setLoaded(true);
            });
    };

    const filteredMembers = useMemo(
        () =>
            members.filter(
                (m) =>
                    m.first_name
                        .toLocaleLowerCase()
                        .includes(query.toLocaleLowerCase().trim()) ||
                    m.last_name
                        .toLocaleLowerCase()
                        .includes(query.toLocaleLowerCase().trim()) ||
                    m.member_id
                        .toLocaleLowerCase()
                        .includes(query.toLocaleLowerCase().trim()),
            ),

        [members, query],
    );
    const filteredGifts = useMemo(
        () =>
            gifts.filter(
                (g) =>
                    g.title
                        .toLocaleLowerCase()
                        .includes(query.toLocaleLowerCase().trim()) ||
                    g.description
                        .toLocaleLowerCase()
                        .includes(query.toLocaleLowerCase().trim()),
            ),

        [gifts, query],
    );

    useEffect(() => {
        switch (fetchType) {
            case "members":
                fetchMemberList();
                break;
            case "gifts":
                fetchGiftList();
                break;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchType]);

    return {
        loading,
        loaded,
        filteredMembers,
        filteredGifts,
    };
};
